// Virtual tours arrive from several places on the property: the property's own tour list, tours hung off
// individual units, and custom fields an integration (Matterport, a 360 vendor) writes a link into.
// String-only so it runs the same during SSG and in the browser.
import { cap, clean, titleCase } from './format';

type AnyRecord = Record<string, unknown>;

export interface RawTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_thumbnail?: string | null;
  virtual_tour_description?: string | null;
  virtual_tour_type?: string | null;
  source?: string | null;
  url_health_error?: string | null;
}

export type TourOrigin = 'property' | 'unit' | 'custom' | 'editor';

export interface GatheredTour {
  key: string;
  title: string;
  url: string;
  thumb: string | null;
  desc: string | null;
  /** Where the tour hangs: "The Jarvis · Unit 204", "Community". */
  where: string | null;
  origin: TourOrigin;
  /** Floor plan the tour walks through, when one matches. */
  planName: string | null;
  isVideo: boolean;
}

interface Unit {
  unit_name?: string | null;
  virtual_tours?: RawTour[] | null;
}

interface Plan {
  floorplan_name?: string | null;
  units?: Unit[] | null;
}

export const normName = (s: string | null | undefined) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

const isHttp = (u: string | null): u is string => !!u && /^https?:\/\//i.test(u);

/** Same tour through two sources (property list and a unit) dedupes on this. */
export function tourKey(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[/?#&]+$/, '')
    .toLowerCase();
}

const CUSTOM_TOUR = /(matterport|virtual_?tour|3d_?tour|360|walk_?through|tour_?3d)/i;
const CUSTOM_NOT_TOUR = /(schedul|self_?guided|booking|book_|appointment|widget|script)/i;

/** custom_matterport_url → "Matterport Tour"; custom_virtual_tour_2_url → "Virtual Tour 2". */
function customTitle(id: string): string {
  const words = id.replace(/^custom_/i, '').replace(/_?url$/i, '').split('_').filter(Boolean);
  const t = words.length ? words.map((w) => (/^\d+d$/i.test(w) ? w.toUpperCase() : cap(w.toLowerCase()))).join(' ') : 'Virtual Tour';
  return /tour|walk ?through/i.test(t) ? t : `${t} Tour`;
}

function customTourFields(p: AnyRecord): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [id, v] of Object.entries(p)) {
    if (id.startsWith('custom_') && CUSTOM_TOUR.test(id) && !CUSTOM_NOT_TOUR.test(id)) out.push([id, String(v ?? '')]);
  }
  for (const f of (p.custom_fields as Array<{ ID?: string; value?: unknown }> | undefined) || []) {
    const id = f?.ID || '';
    if (CUSTOM_TOUR.test(id) && !CUSTOM_NOT_TOUR.test(id) && !(id in p)) out.push([id, String(f.value ?? '')]);
  }
  return out;
}

/** Every working tour on the property, property tours first, then unit tours, then custom-field links. */
export function gatherTours(p: AnyRecord, propertyTours?: RawTour[]): GatheredTour[] {
  const plans = (p.floorplans as Plan[] | undefined) || [];
  const planFor = (title: string | null) =>
    title ? plans.find((f) => normName(f.floorplan_name) && normName(f.floorplan_name) === normName(title)) : undefined;
  const seen = new Set<string>();
  const out: GatheredTour[] = [];
  const add = (t: Omit<GatheredTour, 'key'>) => {
    const key = tourKey(t.url);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ...t, key });
  };
  const usable = (t: RawTour | null | undefined): t is RawTour => !!t && isHttp(clean(t.virtual_tour_url)) && !clean(t.url_health_error);
  const video = (t: RawTour) => /video/i.test(clean(t.virtual_tour_type) || '') || isVideoUrl(clean(t.virtual_tour_url)!);
  const fallbackTitle = (t: RawTour) => (video(t) ? 'Video tour' : '3D tour');

  for (const t of (propertyTours ?? (p.virtual_tours as RawTour[] | undefined)) || []) {
    if (!usable(t)) continue;
    const title = clean(t.virtual_tour_title);
    const plan = planFor(title);
    add({
      title: titleCase(clean(plan?.floorplan_name) || title || fallbackTitle(t)),
      url: clean(t.virtual_tour_url)!,
      thumb: clean(t.virtual_tour_thumbnail),
      desc: clean(t.virtual_tour_description),
      where: plan ? 'Floor plan' : 'Community',
      origin: 'property',
      planName: clean(plan?.floorplan_name),
      isVideo: video(t),
    });
  }

  for (const plan of plans) {
    const planName = clean(plan.floorplan_name);
    for (const unit of plan.units || []) {
      for (const t of unit?.virtual_tours || []) {
        if (!usable(t)) continue;
        const unitName = clean(unit.unit_name);
        add({
          title: titleCase(clean(t.virtual_tour_title) || planName || fallbackTitle(t)),
          url: clean(t.virtual_tour_url)!,
          thumb: clean(t.virtual_tour_thumbnail),
          desc: clean(t.virtual_tour_description),
          where: [planName && titleCase(planName), unitName && `Unit ${unitName}`].filter(Boolean).join(' · ') || null,
          origin: 'unit',
          planName,
          isVideo: video(t),
        });
      }
    }
  }

  for (const [id, value] of customTourFields(p)) {
    const url = clean(value);
    if (!isHttp(url)) continue;
    add({ title: customTitle(id), url, thumb: null, desc: null, where: 'Community', origin: 'custom', planName: null, isVideo: isVideoUrl(url) });
  }

  return out;
}

export type EmbedKind = 'frame' | 'video';

const VIDEO_FILE = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

/** YouTube, Vimeo, a video file, or a vendor's /video/ player link. */
export function isVideoUrl(url: string): boolean {
  return VIDEO_FILE.test(url) || /youtu\.?be|vimeo\.com|\/video\//i.test(url);
}

/**
 * How a tour URL plays inline: YouTube and Vimeo page links become their player URLs, video files play in a
 * <video>, and everything else (Matterport, Kuula, CloudPano, a vendor's own viewer) loads as-is in a frame.
 * Plain http links are upgraded, since an https page blocks them as mixed content.
 */
export function embedFor(url: string, autoplay: boolean): { src: string; kind: EmbedKind } {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return { src: `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0${autoplay ? '&autoplay=1' : ''}`, kind: 'frame' };
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return { src: `https://player.vimeo.com/video/${vimeo[1]}${autoplay ? '?autoplay=1' : ''}`, kind: 'frame' };
  const src = url.replace(/^http:\/\//i, 'https://');
  if (VIDEO_FILE.test(src)) return { src, kind: 'video' };
  if (autoplay && /matterport\.com\/(show|discover)/i.test(src) && !/[?&]play=/.test(src)) {
    return { src: `${src}${src.includes('?') ? '&' : '?'}play=1`, kind: 'frame' };
  }
  return { src, kind: 'frame' };
}
